<script module lang="ts">
  import type { Snippet } from 'svelte';
  import { LayerService } from '../../services/LayerService.svelte';
  import { type AllowedState } from '../content/states.types';

  export const allowedTags = ['section', 'article', 'aside', 'div'] as const;
  export type AllowedTag = (typeof allowedTags)[number];

  export interface Props {
    tag?: AllowedTag;
    state?: AllowedState;
    headline?: Snippet;
    layerAbs?: boolean;
    lightDarker?: boolean;
    children?: Snippet;
  }
</script>

<script lang="ts">
  import { allowedStates } from '../content/states.types';
  import Headline from '../content/Headline.svelte';
  import Content from '../content/Content.svelte';

  // -
  const { tag, state, headline, layerAbs, lightDarker, children }: Props = $props();

  // -
  const DefaultTag = allowedTags[0];
  const ElementTag = $derived(tag && allowedTags.includes(tag) ? tag : DefaultTag);

  // -
  const UseState = $derived(state && allowedStates.includes(state) ? state : undefined);
  const UseColorBackground = $derived(UseState ? 'color-' + UseState + '-background' : 'layout-background');
  const UseColorBorder = $derived(UseState ? 'color-' + UseState + '-dominant' : 'layout-background');
  const UseColorShadow = $derived(UseState ? 'color-' + UseState + '-secondary' : undefined);

  // -
  const Layer = new LayerService();
  const MyLayer = Layer.upContentLay();
</script>

<svelte:element
  this={ElementTag}
  class="tagBox contentLayer contentLayer{MyLayer} {lightDarker ? 'lightDarker' : ''}"
  style:--color-background={`var(--${UseColorBackground})`}
  style:--color-border={`var(--${UseColorBorder})`}
  style:--color-shadow={UseColorShadow ? `var(--${UseColorShadow})` : 'transparent'}>
  {#if headline}
    <Headline state={UseState} size={MyLayer}>
      {@render headline()}
    </Headline>
  {/if}
  {#if children}
    <div>
      <Content {children} {layerAbs} removeMarginTop={true} />
    </div>
  {/if}
</svelte:element>

<style lang="scss">
  .tagBox {
    font-size: var(--font-size);
    background: var(--color-background);
    color: var(--layout-font-color);
    border-radius: 0.5rem;
    padding: calc(var(--font-size) * 0.5) var(--inset-abs) var(--font-size) var(--inset-abs);
    border: 1px solid rgb(from var(--color-border) r g b / 25%);
    box-shadow:
      0 1px 3px rgb(from var(--color-shadow) r g b / 15%),
      0 12px 24px -8px rgb(from var(--layout-font-color) r g b / 10%);

    &.lightDarker {
      background-image: linear-gradient(var(--layout-light-darker), var(--layout-light-darker));
    }
  }
</style>
